import {ref} from 'vue';
import * as Sentry from '@sentry/browser';
import {getPasskeyAssertion, isConditionalMediationAvailable} from '@/js/utils/webauthn.js';
import {parseAllauthErrors} from '@canopyllc/roots/utils/allauthErrors.js';

const LOGIN_URL = '/_allauth/browser/v1/auth/webauthn/login';

// Drives both passkey sign-in paths on the sign-in page:
//   - passkeyLogin()    — the explicit "Sign in with a passkey" button (modal mediation).
//   - startConditional() — WebAuthn conditional UI (passkey autofill). Kicked off on mount
//     so the browser offers discoverable passkeys in the email field's autofill dropdown
//     (the GitHub-style 1Password / Touch ID popup). No-op where unsupported.
//
// Both paths share one ceremony: GET the request options from allauth, sign an assertion,
// POST it back, then onSuccess() navigates away. Only one navigator.credentials.get() may
// be outstanding at a time, so the background conditional request is aborted before the
// button starts its own ceremony (and on unmount) via cancel().
//
// Callbacks keep the composable decoupled from the component's error/redirect handling:
//   onSuccess()      — login succeeded; navigate.
//   onError(errors)  — surface a parsed-allauth or generic error object.
//   onStart()        — optional; the explicit button is about to run (used to clear errors).
export function usePasskeyLogin({api, onSuccess, onError, onStart}) {
  const loading = ref(false);

  let controller = null;

  function cancel() {
    controller?.abort();
    controller = null;
  }

  // Returns true when the error was surfaced, false when swallowed. `silent`
  // suppresses the generic fallback message for the passive conditional flow,
  // where a background failure shouldn't pop an error the user never triggered.
  // A real verification rejection (parsed allauth errors) still surfaces.
  function handleError(err, {silent = false} = {}) {
    // The user dismissed the OS/browser prompt, or we aborted the request.
    if (err?.name === 'NotAllowedError' || err?.name === 'AbortError') return false;
    // allauth answers an already-authenticated session with a 409.
    if (err?.response?.status === 409) {
      onSuccess();
      return true;
    }
    const parsed = parseAllauthErrors(err?.response?.data);
    if (Object.keys(parsed).length > 0) {
      onError(parsed);
      return true;
    }
    Sentry.captureException(err);
    if (!silent) onError({non_field_errors: ['Passkey sign-in failed. Please try again.']});
    return !silent;
  }

  async function runCeremony(mediation) {
    const optsResp = await api.get(LOGIN_URL),
          options = optsResp?.data?.data?.request_options ?? optsResp?.data?.request_options ?? optsResp?.data;

    controller = new AbortController();
    const credential = await getPasskeyAssertion(options, {mediation, signal: controller.signal});
    controller = null;

    await api.post(LOGIN_URL, {credential});
    onSuccess();
  }

  async function passkeyLogin() {
    cancel(); // free the authenticator from any background conditional request first
    onStart?.();
    loading.value = true;
    try {
      await runCeremony(); // modal mediation; onSuccess navigates away, leaving loading set
    } catch (err) {
      handleError(err);
      loading.value = false;
    }
  }

  async function startConditional() {
    if (!(await isConditionalMediationAvailable())) return;
    try {
      await runCeremony('conditional');
    } catch (err) {
      handleError(err, {silent: true});
    }
  }

  return {loading, passkeyLogin, startConditional, cancel};
}
